"use client"

import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Mail, Sparkles, Gift, Bell, CheckCircle } from "lucide-react"
import Seo from "@/seo/seo"
import { useAuth } from "@/context/Auth"

export default function Newsletter() {
  const { subscribeToNewsletter } = useAuth()
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
  })
  const [status, setStatus] = useState("")
  const [errorMessage, setErrorMessage] = useState("")

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
    if (status === "error") {
      setStatus("")
      setErrorMessage("")
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setStatus("sending")
    setErrorMessage("")

    try {
      const fullName = [formData.firstName, formData.lastName]
        .filter(Boolean)
        .join(" ")
        .trim()

      await subscribeToNewsletter(formData.email, fullName)
      setStatus("success")
      setFormData({ firstName: "", lastName: "", email: "" })
    } catch (error) {
      console.error("Newsletter subscription error:", error)
      setStatus("error")

      if (error?.message) {
        setErrorMessage(error.message)
      } else if (error?.response?.data?.message) {
        setErrorMessage(error.response.data.message)
      } else {
        setErrorMessage("Failed to subscribe. Please try again.")
      }
    }
  }

  const benefits = [
    {
      icon: Gift,
      title: "Exclusive Offers",
      description: "Be the first to know about special discounts and promotions",
    },
    {
      icon: Sparkles,
      title: "New Arrivals",
      description: "Get early access to our latest beauty products",
    },
    {
      icon: Bell,
      title: "Beauty Tips",
      description: "Receive expert skincare and haircare advice",
    },
  ]

  return (
    <>
      <Seo
        title="Subscribe to Our Newsletter | Fayoz"
        description="Join our newsletter for exclusive offers, new arrivals, and beauty tips delivered straight to your inbox."
      />
      <div className="min-h-screen bg-gradient-to-br from-primary/5 via-white to-secondary/5">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-r from-primary/10 to-secondary/10 py-16 lg:py-24">
          <div className="container mx-auto px-4 text-center">
            <div className="inline-flex items-center gap-2 bg-secondary/20 text-primary px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Mail className="w-4 h-4" />
              Stay Connected
            </div>
            <h1 className="text-4xl lg:text-6xl font-bold text-primary leading-tight mb-6">
              Join Our <span className="text-secondary">Newsletter</span>
            </h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Subscribe to receive exclusive offers, beauty tips, and be the first to know about our latest products and promotions.
            </p>
          </div>
          <div className="absolute top-0 right-0 w-1/3 h-full bg-gradient-to-l from-secondary/5 to-transparent"></div>
        </section>

        {/* Main Content */}
        <section className="py-16 lg:py-24">
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto grid lg:grid-cols-2 gap-12 items-start">
              {/* Subscription Form */}
              <Card className="shadow-xl border-0 bg-white/80 backdrop-blur-sm">
                <CardHeader className="pb-6">
                  <CardTitle className="text-3xl font-bold text-primary flex items-center gap-3">
                    <Mail className="w-8 h-8 text-secondary" />
                    Subscribe Now
                  </CardTitle>
                  <p className="text-gray-600 text-lg">
                    Enter your details below to join our community.
                  </p>
                </CardHeader>
                <CardContent>
                  {status === "success" ? (
                    <div className="text-center py-8">
                      <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                        <CheckCircle className="w-10 h-10 text-green-600" />
                      </div>
                      <h3 className="text-2xl font-bold text-primary mb-3">
                        You're Subscribed!
                      </h3>
                      <p className="text-gray-600 mb-6">
                        Thank you for subscribing to our newsletter. You'll receive our latest updates and exclusive offers soon.
                      </p>
                      <Button
                        onClick={() => setStatus("")}
                        variant="outline"
                        className="border-primary text-primary hover:bg-primary hover:text-white"
                      >
                        Subscribe Another Email
                      </Button>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} className="space-y-6">
                      <div className="grid md:grid-cols-2 gap-6">
                        <div>
                          <label htmlFor="firstName" className="block text-sm font-semibold text-primary mb-2">
                            First Name <span className="text-gray-400 font-normal">(Optional)</span>
                          </label>
                          <Input
                            id="firstName"
                            name="firstName"
                            type="text"
                            placeholder="John"
                            value={formData.firstName}
                            onChange={handleChange}
                            className="h-12 border-gray-200 focus:border-secondary focus:ring-secondary/20 bg-white/50"
                          />
                        </div>
                        <div>
                          <label htmlFor="lastName" className="block text-sm font-semibold text-primary mb-2">
                            Last Name <span className="text-gray-400 font-normal">(Optional)</span>
                          </label>
                          <Input
                            id="lastName"
                            name="lastName"
                            type="text"
                            placeholder="Doe"
                            value={formData.lastName}
                            onChange={handleChange}
                            className="h-12 border-gray-200 focus:border-secondary focus:ring-secondary/20 bg-white/50"
                          />
                        </div>
                      </div>
                      <div>
                        <label htmlFor="email" className="block text-sm font-semibold text-primary mb-2">
                          Email Address *
                        </label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          placeholder="john.doe@example.com"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          className="h-12 border-gray-200 focus:border-secondary focus:ring-secondary/20 bg-white/50"
                        />
                      </div>
                      <Button
                        type="submit"
                        className="w-full bg-primary hover:bg-primary/90 text-white font-semibold py-4 text-lg h-14 rounded-xl"
                        disabled={status === "sending"}
                      >
                        {status === "sending" ? (
                          <div className="flex items-center gap-2">
                            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                            Subscribing...
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <Mail className="w-5 h-5" />
                            Subscribe to Newsletter
                          </div>
                        )}
                      </Button>
                      {status === "error" && (
                        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                          <p className="text-red-700 text-center font-medium">
                            {errorMessage || "Failed to subscribe. Please try again."}
                          </p>
                        </div>
                      )}
                      <p className="text-sm text-gray-500 text-center">
                        By subscribing, you agree to receive marketing emails from us. You can unsubscribe at any time.
                      </p>
                    </form>
                  )}
                </CardContent>
              </Card>

              {/* Benefits Section */}
              <div className="space-y-6">
                <div className="mb-8">
                  <h2 className="text-2xl font-bold text-primary mb-3">
                    Why Subscribe?
                  </h2>
                  <p className="text-gray-600">
                    Join thousands of beauty enthusiasts who receive our exclusive content.
                  </p>
                </div>

                {benefits.map((benefit, index) => (
                  <Card
                    key={index}
                    className="shadow-lg border-0 bg-white/80 backdrop-blur-sm hover:shadow-xl transition-shadow duration-300"
                  >
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4">
                        <div className="w-14 h-14 bg-gradient-to-br from-secondary/20 to-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
                          <benefit.icon className="w-7 h-7 text-secondary" />
                        </div>
                        <div>
                          <h3 className="font-bold text-primary text-lg mb-1">
                            {benefit.title}
                          </h3>
                          <p className="text-gray-600">
                            {benefit.description}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}

                <div className="bg-gradient-to-r from-primary/10 to-secondary/10 rounded-2xl p-6 mt-8">
                  <div className="flex items-center gap-3 mb-3">
                    <Sparkles className="w-6 h-6 text-secondary" />
                    <h3 className="font-bold text-primary text-lg">Special Welcome Gift</h3>
                  </div>
                  <p className="text-gray-600">
                    New subscribers receive an exclusive discount code for their first purchase!
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  )
}
